// ─────────────────────────────────────────────────────────────────────────
// formDecisionDialog — the popups the Add form asks questions through.
//
// WHAT WAS WRONG WITH THE OLD ONES
// They were bare Modal.confirm calls: a title, one run-on sentence, "OK" and
// "Cancel". Three problems, all of them the same problem — the user could not
// see what they were deciding about:
//
//   • the FACTS were buried in prose ("…— Asha · Rao — last updated about 2
//     months ago. Continue to update…"), so the two things that actually decide
//     the answer (who, how stale) had to be read out of a paragraph;
//   • "OK" names no outcome, and neither choice here is obviously the default;
//   • nothing distinguished "we found a possible duplicate" from "this will
//     overwrite your work" — different stakes, identical dialog.
//
// WHAT THIS DOES INSTEAD
// A tone-coloured icon states the kind of decision at a glance; the heading is
// clearly heavier than the body; the facts sit in a scannable card ABOVE the
// prose; and the buttons name their outcomes. The tone drives colour only —
// never meaning on its own, so it still reads correctly in monochrome.
//
// Imperative on purpose: it is called from hooks and event handlers that need
// to await an answer, so it returns a Promise<boolean> exactly like
// Modal.confirm did, and every existing call site keeps its shape.
// ─────────────────────────────────────────────────────────────────────────
import { Modal } from 'antd';
import {
  ExclamationCircleFilled,
  InfoCircleFilled,
  FileTextOutlined,
  UserOutlined,
} from '@ant-design/icons';
import './formDecisionDialog.css';

const TONES = {
  // "We think these are the same person" — a judgement, not a failure.
  duplicate: { className: 'fdd-tone-amber', Icon: UserOutlined },
  // "This will replace what you typed" — a real risk to work already done.
  overwrite: { className: 'fdd-tone-amber', Icon: ExclamationCircleFilled },
  // "Shall I tidy this for you?" — no stakes at all.
  suggestion: { className: 'fdd-tone-blue', Icon: InfoCircleFilled },
  file: { className: 'fdd-tone-blue', Icon: FileTextOutlined },
};

/**
 * openFormDecision — ask a question and resolve to the user's answer.
 *
 * @param {object}   opts
 * @param {string}   opts.tone      duplicate | overwrite | suggestion | file
 * @param {string}   opts.title     the heading — say the SITUATION, not "Are you sure?"
 * @param {string}   opts.body      one or two sentences of context
 * @param {Array}   [opts.facts]    [{ label, value }] — shown as a scannable card
 * @param {string}   opts.okText    names the outcome, never "OK"
 * @param {string}   opts.cancelText
 * @param {boolean} [opts.danger]   style the primary action as destructive
 * @param {string}  [opts.footnote] a quiet line under the buttons
 * @returns {Promise<boolean>}
 */
export function openFormDecision({
  tone = 'suggestion',
  title,
  body,
  facts = [],
  okText = 'Continue',
  cancelText = 'Cancel',
  danger = false,
  footnote,
} = {}) {
  const { className, Icon } = TONES[tone] ?? TONES.suggestion;
  const usableFacts = facts.filter((f) => f && f.value !== undefined && f.value !== null && String(f.value).trim() !== '');

  return new Promise((resolve) => {
    Modal.confirm({
      // antd's own icon is suppressed: this dialog renders its own, sized and
      // coloured with the heading rather than floating beside the body.
      icon: null,
      centered: true,
      width: 480,
      className: `fdd-modal ${className}`,
      okText,
      cancelText,
      okButtonProps: { danger, size: 'large' },
      cancelButtonProps: { size: 'large' },
      content: (
        <div className="fdd">
          <div className="fdd-head">
            <span className="fdd-icon" aria-hidden="true"><Icon /></span>
            <h3 className="fdd-title">{title}</h3>
          </div>

          {/* The facts come FIRST and are scannable. This is the part that
              actually answers "is this the same person?" — reading it out of a
              sentence is work the reader should not have to do. */}
          {usableFacts.length > 0 && (
            <dl className="fdd-facts">
              {usableFacts.map((f) => (
                <div className="fdd-fact" key={f.label}>
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {body && <p className="fdd-body">{body}</p>}
          {footnote && <p className="fdd-footnote">{footnote}</p>}
        </div>
      ),
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

/**
 * openFormNotice — a one-way message (the module refuses to continue).
 * Same shell, single action, so a block and a choice look like relatives
 * rather than two unrelated dialogs.
 */
export function openFormNotice({ tone = 'duplicate', title, body, facts = [], okText = 'Go back' } = {}) {
  const { className, Icon } = TONES[tone] ?? TONES.suggestion;
  const usableFacts = facts.filter((f) => f && String(f.value ?? '').trim() !== '');

  return new Promise((resolve) => {
    Modal.warning({
      icon: null,
      centered: true,
      width: 480,
      className: `fdd-modal ${className}`,
      okText,
      okButtonProps: { size: 'large' },
      content: (
        <div className="fdd">
          <div className="fdd-head">
            <span className="fdd-icon" aria-hidden="true"><Icon /></span>
            <h3 className="fdd-title">{title}</h3>
          </div>
          {usableFacts.length > 0 && (
            <dl className="fdd-facts">
              {usableFacts.map((f) => (
                <div className="fdd-fact" key={f.label}>
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {body && <p className="fdd-body">{body}</p>}
        </div>
      ),
      onOk: () => resolve(true),
    });
  });
}
